import { useState, useEffect } from 'react';
import { StoreProvider, useStore } from './store';
import { ThemeProvider, useTheme } from './ThemeContext';
import Login from './components/Login';
import Tickets from './components/Tickets';
import FacturasModule from './components/Facturas';
import Reparaciones from './components/Reparaciones';
import { Clientes, Articulos, Pedidos, Apuntes, Proveedores, FacturasProveedor, ImportadorFacturas, RevisarRemitentes, EspaciosTaller } from './components/Misc';
import Configuracion from './components/Configuracion';
import Utilidades from './components/Utilidades';
import WhatsAppChat from './components/WhatsAppChat';
import { Modal, Input, Btn, useLocalStorage } from './components/UI';

// Breakpoint pa tablet en portrait (iPad 11" portrait ~834px, no afecta landscape ni escritorio)
function useIsNarrow(maxWidth = 900) {
  const [narrow, setNarrow] = useState(() => window.matchMedia(`(max-width: ${maxWidth}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => { mq.removeEventListener('change', onChange); window.removeEventListener('orientationchange', onChange); };
  }, [maxWidth]);
  return narrow;
}

// roles: undefined = todos, array = solo esos roles
const NAV = [
  { section:'Ventas', items:[
    { id:'tickets',      icon:'🧾', label:'Tickets',       roles:['admin','vendedor'] },
    { id:'facturas',     icon:'📄', label:'Facturas',      roles:['admin','vendedor'] },
    { id:'proformas',       icon:'📋', label:'Proformas',          roles:['admin','vendedor'] },
    { id:'presupuestos',    icon:'🧮', label:'Presupuestos',       roles:['admin','vendedor'] },
    { id:'pedidosCliente',  icon:'📥', label:'Pedidos cliente',    roles:['admin','vendedor'] },
  ]},
  { section:'Taller', items:[
    { id:'reparaciones',     icon:'🔧', label:'Reparaciones',      roles:['admin','tecnico'] },
    { id:'espaciosTaller',   icon:'🗂️', label:'Espacios taller',   roles:['admin','tecnico'] },
  ]},
  { section:'Gestión', items:[
    { id:'clientes',  icon:'👥', label:'Clientes',   roles:['admin','vendedor','tecnico'] },
    { id:'articulos', icon:'📦', label:'Artículos',  roles:['admin','vendedor'] },
    { id:'apuntes',   icon:'⏰', label:'Recordatorios', roles:['admin','vendedor','tecnico'] },
  ]},
  { section:'Sistema', items:[
    { id:'config',      icon:'⚙️', label:'Configuración', roles:['admin'] },
    { id:'utilidades',   icon:'🛠️', label:'Utilidades',      roles:['admin','vendedor'] },
  ]},
];

function CambiarPassword({ onClose }) {
  const { cambiarPassword } = useStore();
  const [actual, setActual] = useState('');
  const [nueva,  setNueva]  = useState('');
  const [rep,    setRep]    = useState('');
  const [err,    setErr]    = useState('');
  const [ok,     setOk]     = useState(false);

  async function save() {
    if (!actual || !nueva) return setErr('Rellena todos los campos');
    if (nueva !== rep)     return setErr('Las contraseñas nuevas no coinciden');
    if (nueva.length < 6)  return setErr('Mínimo 6 caracteres');
    setErr('');
    try {
      await cambiarPassword(actual, nueva);
      setOk(true);
      setTimeout(onClose, 1500);
    } catch(e) { setErr(e.message); }
  }

  return (
    <Modal title="Cambiar contraseña" onClose={onClose} width={380}>
      {ok ? (
        <div style={{ textAlign:'center', padding:20, color:'#0e9f6e', fontSize:15, fontWeight:600 }}>✓ Contraseña actualizada</div>
      ) : (
        <>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
            <Input label="Contraseña actual" type="password" value={actual} onChange={setActual} />
            <Input label="Nueva contraseña"  type="password" value={nueva}  onChange={setNueva} />
            <Input label="Repetir nueva"     type="password" value={rep}    onChange={setRep} />
          </div>
          {err && <div style={{ background:'#fee2e2', color:'#991b1b', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:12 }}>{err}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
            <Btn variant="primary" onClick={save}>Guardar</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

function Sidebar({ active, setActive, modoTablet, onToggleModoTablet }) {
  const { data, auth, logout, downloadBackup } = useStore();
  const { isDark, toggleTheme } = useTheme();
  const [showPw, setShowPw] = useState(false);

  const repsListas = (data.reparaciones||[]).filter(r => r.estado==='Lista recogida').length;
  const stockBajo  = (data.articulos||[]).filter(a => a.stock <= a.stockMin).length;

  const surface = isDark ? '#141412' : '#fff';
  const border  = isDark ? '#2a2a26' : '#e5e7eb';
  const ink     = isDark ? '#f2f2ee' : '#111827';
  const muted   = isDark ? '#8f8f86' : '#9ca3af';
  const primary = isDark ? '#a3e635' : '#1a56db';
  const activeBg = isDark ? 'rgba(163,230,53,0.14)' : '#e8f0fe';

  return (
    <div style={{ width:196, background:surface, borderRight:`1px solid ${border}`, display:'flex', flexDirection:'column', flexShrink:0, height:'100vh', position:'sticky', top:0 }}>
      <div style={{ padding:'14px 14px 10px', borderBottom:`1px solid ${border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:9 }}>
          <div style={{ width:34, height:34, background:primary, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}><img src="/favicon.ico" alt="r" style={{ width:26, height:26, objectFit:'contain' }} /></div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700, color:ink, lineHeight:1.2 }}>Root Informática</div>
            <div style={{ fontSize:11, color:muted }}>Sistema de gestión</div>
          </div>
          <button onClick={toggleTheme} title={isDark ? 'Tema claro' : 'Tema oscuro'} style={{ background:'none', border:'none', cursor:'pointer', fontSize:15, color:muted, padding:2, flexShrink:0 }}>
            {isDark ? '☀️' : '🌙'}
          </button>
        </div>
        {onToggleModoTablet && (
          <button onClick={onToggleModoTablet} title="Forzar menú colapsable, aunque la pantalla no se detecte como estrecha"
            style={{ marginTop:8, width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'5px 0', border:`1px solid ${modoTablet ? primary : border}`, borderRadius:7, background: modoTablet ? activeBg : 'none', color: modoTablet ? primary : muted, fontSize:11, fontWeight:600, cursor:'pointer' }}>
            📱 Modo tablet {modoTablet ? 'ON' : 'OFF'}
          </button>
        )}
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'6px 8px' }}>
        {NAV.map(sec => (
          <div key={sec.section} style={{ marginBottom:2 }}>
            <div style={{ fontSize:10, fontWeight:700, color:muted, textTransform:'uppercase', letterSpacing:'.06em', padding:'6px 14px 2px' }}>{sec.section}</div>
            {sec.items.filter(item => !item.roles || item.roles.includes(auth.rol||'admin')).map(item => (
              <div key={item.id} style={{ position:'relative' }}>
                <button onClick={() => setActive(item.id)} style={{
                  display:'flex', alignItems:'center', gap:8, width:'100%', padding:'7px 14px',
                  background: active===item.id ? activeBg : 'transparent',
                  color: active===item.id ? primary : muted,
                  fontWeight: active===item.id ? 700 : 400,
                  border:'none', borderRadius:8, cursor:'pointer', fontSize:13, textAlign:'left', fontFamily:'inherit',
                }}>
                  <span>{item.icon}</span>{item.label}
                </button>
                {item.id==='reparaciones' && repsListas>0 && (
                  <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'#0e9f6e', color:'#fff', borderRadius:20, fontSize:10, fontWeight:700, padding:'1px 6px' }}>{repsListas}</span>
                )}
                {item.id==='articulos' && stockBajo>0 && (
                  <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'#f59e0b', color:'#fff', borderRadius:20, fontSize:10, fontWeight:700, padding:'1px 6px' }}>{stockBajo}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer: usuario + acciones */}
      <div style={{ padding:'10px 10px', borderTop:`1px solid ${border}`, fontSize:11 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8, padding:'6px 8px', background:isDark ? '#1c1c18' : '#f9fafb', borderRadius:8 }}>
          <span style={{ fontSize:16 }}>👤</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, color:ink, fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{auth.nombre}</div>
            <button onClick={() => setShowPw(true)} style={{ background:'none', border:'none', color:muted, fontSize:10, cursor:'pointer', padding:0, textAlign:'left' }}>
              Cambiar contraseña
            </button>
          </div>
          <button onClick={logout} title="Cerrar sesión" style={{ background:'none', border:'none', cursor:'pointer', fontSize:15, color:muted, padding:2 }}>⏏</button>
        </div>
        <button onClick={downloadBackup} style={{ width:'100%', background:'none', border:`1px solid ${border}`, borderRadius:7, padding:'5px 0', fontSize:11, color:muted, cursor:'pointer' }}>
          💾 Descargar backup
        </button>
      </div>

      {showPw && <CambiarPassword onClose={() => setShowPw(false)} />}
    </div>
  );
}

const PAGE_TITLES = {
  tickets:'Tickets', facturas:'Facturas', proformas:'Proformas', presupuestos:'Presupuestos',
  pedidosCliente:'Pedidos de cliente',
  reparaciones:'Reparaciones — Taller', espaciosTaller:'Espacios taller', pedidos:'Pedidos a proveedor', importadorFacturas:'Importar facturas desde email', revisarRemitentes:'Revisar remitentes',
  clientes:'Clientes', proveedores:'Proveedores', facturasProveedor:'Facturas de proveedor', articulos:'Artículos / Stock', apuntes:'Recordatorios', config:'Configuración', utilidades:'Utilidades', entrenamientoIA:'🤖 PACO', whatsappChat:'💬 WhatsApp',
};

function MainContent({ active, kiosk, onToggleKiosk, isNarrow, onOpenSidebar }) {
  const panels = {
    tickets:      <Tickets />,
    facturas:     <FacturasModule tipoKey="facturas" />,
    proformas:    <FacturasModule tipoKey="proformas" />,
    presupuestos:   <FacturasModule tipoKey="presupuestos" />,
    pedidosCliente: <FacturasModule tipoKey="pedidosCliente" />,
    reparaciones:   <Reparaciones />,
    espaciosTaller: <EspaciosTaller />,
    pedidos:      <Pedidos />,
    clientes:     <Clientes />,
    articulos:    <Articulos />,
    apuntes:           <Apuntes />,
    proveedores:       <Proveedores />,
    facturasProveedor:  <FacturasProveedor />,
    importadorFacturas: <ImportadorFacturas />,
    revisarRemitentes:  <RevisarRemitentes />,
    config:       <Configuracion />,
    utilidades:      <Utilidades />,
    whatsappChat: <WhatsAppChat />,
  };
  const { isDark } = useTheme();
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <div style={{ background:isDark?'#141412':'#fff', borderBottom:`1px solid ${isDark?'#2a2a26':'#e5e7eb'}`, padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {isNarrow && !kiosk && (
            <button onClick={onOpenSidebar} title="Menú" style={{ background:'none', border:'none', fontSize:22, color:isDark?'#f2f2ee':'#111827', cursor:'pointer', padding:'2px 4px', lineHeight:1 }}>☰</button>
          )}
          <h1 style={{ fontSize:17, fontWeight:700, color:isDark?'#f2f2ee':'#111827' }}>{PAGE_TITLES[active]}</h1>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ fontSize:12, color:isDark?'#8f8f86':'#9ca3af' }}>{new Date().toLocaleDateString('es-ES',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</div>
          {active==='espaciosTaller' && (
            <button onClick={onToggleKiosk} title={kiosk ? 'Salir de pantalla completa' : 'Pantalla completa (sin menú, para monitor aparte)'} style={{
              display:'flex', alignItems:'center', gap:6, background:'none', border:`1px solid ${isDark?'#2a2a26':'#e5e7eb'}`, borderRadius:7,
              padding:'5px 10px', fontSize:12, color:isDark?'#8f8f86':'#6b7280', cursor:'pointer', fontFamily:'inherit',
            }}>
              {kiosk ? '✕ Salir' : '⛶ Pantalla completa'}
            </button>
          )}
        </div>
      </div>
      <div style={{ flex:1, padding:24, overflowY:'auto' }}>
        {panels[active]}
      </div>
    </div>
  );
}

function LoadingScreen() {
  const { isDark } = useTheme();
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:isDark?'#0b0b09':'#f0f4f8', flexDirection:'column', gap:16 }}>
      <div style={{ width:48, height:48, background:isDark?'#a3e635':'#1a56db', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}><img src="/favicon.ico" alt="r" style={{ width:36, height:36, objectFit:'contain' }} /></div>
      <div style={{ fontSize:14, color:isDark?'#8f8f86':'#6b7280', fontFamily:'-apple-system,sans-serif' }}>Cargando datos del servidor...</div>
    </div>
  );
}

function AppInner() {
  const { auth, loading, loadAll } = useStore();
  const rol = auth.rol || 'admin';
  const defaultPage = rol === 'tecnico' ? 'reparaciones' : 'tickets';
  const [active, setActive] = useState(defaultPage);
  const [kiosk, setKiosk] = useState(false);
  const isNarrowAuto = useIsNarrow(900);
  const [modoTabletManual, setModoTabletManual] = useLocalStorage('modoTabletManual', false);
  const isNarrow = isNarrowAuto || modoTabletManual;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleKiosk = () => {
    if (!kiosk) {
      document.documentElement.requestFullscreen?.().catch(() => {});
      setKiosk(true);
    } else {
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
      setKiosk(false);
    }
  };

  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setKiosk(false); };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const cambiarSeccion = (sec) => {
    try {
      Object.keys(localStorage).filter(k => k.startsWith('search_')).forEach(k => localStorage.removeItem(k));
    } catch {}
    setActive(sec);
    setSidebarOpen(false);
  };

  useEffect(() => { if (!isNarrow) setSidebarOpen(false); }, [isNarrow]);

  useEffect(() => {
    const onFocus = () => { if (!document.hidden && auth.ok && !(parseInt(document.body.dataset.modals || '0', 10) > 0)) loadAll({ silent: true }); };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [auth.ok, loadAll]);

  // Navegación entre secciones desde fuera del Sidebar (ej. botón "Editar
  // reparación" en Espacios taller) — la sección destino lee el id pendiente
  // de sessionStorage al montar.
  useEffect(() => {
    const onIrA = e => cambiarSeccion(e.detail.seccion);
    window.addEventListener('ri:ir-a-seccion', onIrA);
    return () => window.removeEventListener('ri:ir-a-seccion', onIrA);
  }, []);

  const { isDark } = useTheme();
  if (loading) return <LoadingScreen />;
  if (!auth.ok) return <Login />;

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:isDark?'#0b0b09':'#f0f2f5' }}>
      {!kiosk && (isNarrow ? (
        <>
          {sidebarOpen && (
            <div onClick={() => setSidebarOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1998 }} />
          )}
          <div style={{ position:'fixed', top:0, left:0, height:'100vh', zIndex:1999, transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition:'transform .22s ease', boxShadow: sidebarOpen ? '4px 0 20px rgba(0,0,0,.3)' : 'none' }}>
            <Sidebar active={active} setActive={cambiarSeccion} modoTablet={modoTabletManual} onToggleModoTablet={() => setModoTabletManual(!modoTabletManual)} />
          </div>
        </>
      ) : (
        <Sidebar active={active} setActive={cambiarSeccion} modoTablet={modoTabletManual} onToggleModoTablet={() => setModoTabletManual(!modoTabletManual)} />
      ))}
      <MainContent active={active} kiosk={kiosk} onToggleKiosk={toggleKiosk} isNarrow={isNarrow} onOpenSidebar={() => setSidebarOpen(true)} />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <StoreProvider>
        <AppInner />
      </StoreProvider>
    </ThemeProvider>
  );
}

export default App;
